﻿import { LngLatLike, Map } from "mapbox-gl";
import Layer, { Options } from 'ol/layer/Layer';
import { FrameState } from 'ol/Map';
import { transform } from "ol/proj";
import { MobxReactionDisposer } from "../models/MobxReactionDisposer";

class MapBoxLayer extends Layer {

    protected mobxDisposer = new MobxReactionDisposer();

    constructor(
        public readonly map: Map,
        layersOptions?: Options,
        adjustZoom?: number
    ) {
        super({
            ...layersOptions,
            render: fs => this.renderMapBox(fs, adjustZoom)
        });

        // Required because sometimes canvas remains visible while layer is not
        this.on('change:visible', this.updateMapCanvas);
    }

    public dispose() {
        this.mobxDisposer.clear();
        this.un('change:visible', this.updateMapCanvas);
        super.dispose();
    }

    private updateMapCanvas = () => {
        const canvas = this.map.getCanvas();
        const visible = this.getVisible();
        canvas.style.display = visible ? 'block' : 'none';        
    }

    private renderMapBox(frameState: FrameState, adjustZoom: number | undefined) {

        var canvas = this.map.getCanvas();
        if (frameState.size[0] !== canvas.width || frameState.size[1] !== canvas.height) {
            this.map.resize();
        }

        var viewState = frameState.viewState;

        var visible = this.getVisible();
        canvas.style.display = visible ? 'block' : 'none';

        var opacity = this.getOpacity();
        canvas.style.opacity = opacity.toString();
        canvas.style.position = 'absolute';

        // adjust view parameters in mapbox
        var rotation = viewState.rotation;
        if (rotation) {
            this.map.rotateTo(-rotation * 180 / Math.PI, {
                animate: false
            });
        }
        this.map.jumpTo({
            center: transform(viewState.center, 'EPSG:3857', 'EPSG:4326') as LngLatLike,
            zoom: viewState.zoom + (adjustZoom ?? -1)
        });

        // cancel the scheduled update & trigger synchronous redraw
        // see https://github.com/mapbox/mapbox-gl-js/issues/7893#issue-408992184
        // NOTE: THIS MIGHT BREAK WHEN UPDATING MAPBOX
        // doesn't seem te be required in newer Mapbox versions...
        try {
            const m = this.map as any;
            if (m._frame) {
                m._frame.cancel();
                m._frame = null;
            }

            m._render();
        }
        catch {
        }

        // returning canvas container instead of canvas will allow mapbox interactions
        return this.map.getCanvasContainer();
    }
}

export default MapBoxLayer;